import * as React from 'react';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BadgeHelp, X } from 'lucide-react';
import { useMedicareQuote } from '../../../../shared';
import { useAppContext } from '@coverright/shared/contexts';
import { Preloader } from '@coverright/features';
import { DoctorsTab } from '@coverright/widgets/doctors-drugs-selection/ui/doctors-tab';
import { DrugsTab } from '@coverright/widgets/doctors-drugs-selection/ui/drugs-tab';
import { PharmacyTab } from '@coverright/widgets/doctors-drugs-selection/ui/pharmacy-tab';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DoctorsDrugsTab = 0 | 1 | 2;

interface Props {
  open: boolean;
  initialTab?: DoctorsDrugsTab;
  onClose: () => void;
}

// ─── Tab config ───────────────────────────────────────────────────────────────

const TABS: { label: string; subtitle: string; infoTitle: string; infoText: string }[] = [
  {
    label:     'Doctors',
    subtitle:  'Add your doctors to check plan networks.',
    infoTitle: "Can't find your doctor?",
    infoText:  "Don't worry if you can't find your doctor! Our licensed insurance agents can conduct a more detailed search before finalizing your plan. Enter what you can and we can do an initial search for those that are showing. However, there may not be a plan in your area that covers all your doctors.",
  },
  {
    label:     'Drugs',
    subtitle:  'Add your prescriptions to estimate and compare plan costs.',
    infoTitle: "Can't find your drug?",
    infoText:  "Not all drugs are covered by Medicare plans. If you can't find a prescription drug that's important to you, continue entering the ones available and then speak to one of our licensed insurance agents. However, there may not be a plan in your area that covers all your drugs.",
  },
  {
    label:     'Pharmacy',
    subtitle:  'Select your pharmacy to check for lower cost-sharing.',
    infoTitle: 'How is this used?',
    infoText:  "Medicare plans may have lower prices for 'preferred' pharmacies and mail order. By telling us how you fill your prescriptions we can give you more accurate cost estimates.",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const DoctorsDrugsModal = ({ open, initialTab = 0, onClose }: Props) => {
  const quote = useMedicareQuote();
  const [appState, saveToAppState] = useAppContext();
  const [tab, setTab] = useState<DoctorsDrugsTab>(initialTab);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Doctors/drugs added here bypass the onboarding "Do you have doctors/drugs?"
  // step, where appState.hasDoctors/hasDrugs is normally set. The match score
  // reads that flag, so without it items added via this modal are never credited.
  useEffect(() => {
    if ((quote?.preferredDoctors?.length ?? 0) > 0 && !appState.hasDoctors) {
      saveToAppState({ hasDoctors: true });
    }
  }, [quote?.preferredDoctors?.length, appState.hasDoctors, saveToAppState]);

  useEffect(() => {
    if ((quote?.preferredDrugs?.length ?? 0) > 0 && !appState.hasDrugs) {
      saveToAppState({ hasDrugs: true });
    }
  }, [quote?.preferredDrugs?.length, appState.hasDrugs, saveToAppState]);

  const cfg = TABS[tab];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: '16px', m: { xs: 1, sm: 2 }, maxHeight: '95vh' } }}
    >
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}
      >
        <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
          Update your info
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ bgcolor: '#f9fafb', '&:hover': { bgcolor: '#f3f4f6' }, borderRadius: '50%' }}
        >
          <X size={16} />
        </IconButton>
      </Stack>

      {/* ── Scrollable body ── */}
      <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: 4, overflowY: 'auto' }}>
        {!quote ? (
          <Preloader sx={{ height: 400 }} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Segmented tab selector */}
            <TabBar>
              {TABS.map((t, i) => (
                <TabBtn
                  key={t.label}
                  active={tab === i}
                  onClick={() => setTab(i as DoctorsDrugsTab)}
                >
                  {t.label}
                </TabBtn>
              ))}
            </TabBar>

            {/* Subtitle */}
            <Typography sx={{ fontSize: '0.875rem', color: '#4b5563' }}>
              {cfg.subtitle}
            </Typography>

            {/* Tab content — font scale-down via CSS cascade */}
            <TabContentScale>
              {tab === 0 && <DoctorsTab quote={quote} hideNoDoctorsMessage />}
              {tab === 1 && <DrugsTab   quote={quote} hideNoDrugsMessage />}
              {tab === 2 && <PharmacyTab quote={quote} />}
            </TabContentScale>

            {/* Info card */}
            <InfoCard>
              <BadgeHelp size={20} color="#009689" style={{ flexShrink: 0, marginTop: 2 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#111827', mb: 0.75 }}>
                  {cfg.infoTitle}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>
                  {cfg.infoText}
                </Typography>
              </Box>
            </InfoCard>

          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const TabBar = styled(Box)({
  display: 'flex',
  gap: 4,
  backgroundColor: '#f3f4f6',
  borderRadius: 12,
  padding: 6,
});

const TabBtn = styled('button')<{ active: boolean }>(({ active }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1,
  transition: 'background-color 150ms, color 150ms, box-shadow 150ms',
  ...(active
    ? {
        backgroundColor: '#ffffff',
        color: '#111827',
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.12)',
      }
    : {
        backgroundColor: 'transparent',
        color: '#0a6c84',
        '&:hover': { backgroundColor: '#eaf5f8' },
      }),
}));

const TabContentScale = styled(Box)({
  // Scale down all Typography inside without touching library source
  '& .MuiTypography-root': { fontSize: '0.8125rem' },   // 13px base
  '& .MuiTypography-h4':   { fontSize: '0.9375rem' },   // section headings stay slightly larger
  '& .MuiButton-root':     { fontSize: '0.8125rem' },   // button labels
  '& input, & select':     { fontSize: '0.8125rem' },   // form controls
  '& .pharmacy-btn, & .drug-btn, & .doctor-btn':     { height: 37 },
  '& .doctors-drugs-item':     { padding: '12px 16px' },
});

const InfoCard = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 12,
  padding: 20,
  backgroundColor: '#f0f7f9',
  border: '1px solid #e1eff2',
  borderRadius: 12,
});
